import {ComputerDesktopIcon, MicrophoneIcon, SpeakerXMarkIcon} from '@heroicons/react/20/solid';
import {useState, useCallback} from 'react';
import * as U from './util';
import {AudioMeter} from './audio-meter';
import * as aircoreMedia from '@aircore/aircore-media';
import {DropMenu, DropMenuItem} from './drop-menu';
import {v4 as uuid4} from 'uuid';
import {Video} from './camera-video';
import {
  emitEvent,
  useEventHandler,
  Event,
  LocalStreamMuteStateChange,
  LocalStreamVadStateChange,
  UserUpdateAdditionalTagsEvent,
} from './events';
import {
  BUTTON_STYLE_STREAM_PANEL,
  BUTTON_STYLE_STREAM_PANEL_LEFT,
  BUTTON_STYLE_STREAM_PANEL_RIGHT,
  PANEL_STYLE_STREAM,
} from './styles';

interface LocalStreamProps {
  stream: aircoreMedia.LocalStream;
  handleAddVideo: () => void;
  additionalTags: string[];
}

export function LocalStreamPanel({stream, handleAddVideo, additionalTags}: LocalStreamProps) {
  const [isAudioMuted, setIsAudioMuted] = useState(stream.audioMuted);
  const [isVideoMuted, setIsVideoMuted] = useState(stream.videoMuted);
  const [isExtractingFrames, setFrameExtraction] = useState(stream.isExtractingFrames);
  const [isVoiceDetected, setIsVoiceDetected] = useState(stream.voiceActivity);
  const [hasVideoSource, setHasVideoSource] = useState(!!(stream.videoCamera ?? stream.screenShare));
  const [frameElementId] = useState(uuid4());

  const audioLevelGetter = () => stream.audioLevel;

  useEventHandler(
    Event.BIGSUR_LOCAL_STREAM_AUDIO_MUTE_CHANGED,
    (change: LocalStreamMuteStateChange) => {
      if (change.stream === stream) {
        setIsAudioMuted(stream.audioMuted);
      }
    },
    [stream]
  );
  useEventHandler(
    Event.BIGSUR_LOCAL_STREAM_VIDEO_MUTE_CHANGED,
    (change: LocalStreamMuteStateChange) => {
      if (change.stream === stream) {
        setHasVideoSource(!!(stream.videoCamera ?? stream.screenShare));
        setIsVideoMuted(stream.videoMuted);
      }
    },
    [stream]
  );
  useEventHandler(
    Event.BIGSUR_LOCAL_STREAM_VAD_CHANGED,
    (change: LocalStreamVadStateChange) => {
      U.debug(`Local stream VAD changed: ${change.state.voiceActivity}`);
      setIsVoiceDetected(change.state.voiceActivity);
    },
    [stream]
  );

  const handleMuteAudio = useCallback(() => {
    emitEvent(Event.USER_MUTE_LOCAL_AUDIO, stream);
  }, [stream]);

  const handleUnmuteAudio = useCallback(() => {
    emitEvent(Event.USER_UNMUTE_LOCAL_AUDIO, stream);
  }, [stream]);

  const handleMuteVideo = useCallback(() => {
    emitEvent(Event.USER_MUTE_LOCAL_VIDEO, stream);
  }, [stream]);

  const handleUnmuteVideo = useCallback(() => {
    emitEvent(Event.USER_UNMUTE_LOCAL_VIDEO, stream);
  }, [stream]);

  const handleStopPublish = useCallback(() => {
    emitEvent(Event.USER_STOP_PUBLISH, stream);
  }, [stream]);

  function handleUpdateAdditionalTags() {
    const eventData: UserUpdateAdditionalTagsEvent = {stream, additionalTags};
    emitEvent(Event.USER_UPDATE_ADDITIONAL_TAGS, eventData);
  }

  const handleLogStats = useCallback(() => {
    emitEvent(Event.USER_LOG_STREAM_STATS, stream);
  }, [stream]);

  const setFrameExtractionCallback = (bool: boolean) => {
    U.log(`Local stream frame extraction ${bool ? 'enabled' : 'disabled'}`);
    setFrameExtraction(bool);
  };

  const handleStartFrameExtraction = (frameRate: number) => {
    if (!isExtractingFrames) {
      emitEvent(Event.USER_START_FRAME_EXTRACTION_LOCAL_VIDEO, {
        stream,
        frameRate,
        frameElementId,
        setFrameExtraction: setFrameExtractionCallback,
      });
    }
  };
  const handleStopFrameExtraction = () => {
    if (isExtractingFrames) {
      emitEvent(Event.USER_STOP_FRAME_EXTRACTION_LOCAL_VIDEO, {
        stream,
        frameElementId,
        setFrameExtraction: setFrameExtractionCallback,
      });
    }
  };

  const outline = isVoiceDetected ? 'outline outline-2 outline-green-500' : null;

  const FRAME_BUTTONS: DropMenuItem[] = [
    {value: 'Frame 500ms', callback: () => handleStartFrameExtraction(500)},
    {value: 'Frame 1s', callback: () => handleStartFrameExtraction(1000)},
    {value: 'Frame 5s', callback: () => handleStartFrameExtraction(5000)},
  ];

  return (
    <div className={`${PANEL_STYLE_STREAM} ${outline}`}>
      <div className="w-auto h-auto rounded-t-md sm:rounded-l-md sm:rounded-tr-none p-2 flex flex-col justify-around gap-1">
        <p className="text-center text-sm text-white bold">Audio</p>
        <span>
          <button className={BUTTON_STYLE_STREAM_PANEL_LEFT} onClick={handleMuteAudio} hidden={stream.isScreenShare}>
            Mute
          </button>
          <button className={BUTTON_STYLE_STREAM_PANEL_RIGHT} onClick={handleUnmuteAudio} hidden={stream.isScreenShare}>
            Unmute
          </button>
        </span>
        <p className="text-center text-sm text-white bold">Video</p>
        {hasVideoSource ? (
          <span>
            <button className={BUTTON_STYLE_STREAM_PANEL_LEFT} onClick={handleMuteVideo} hidden={stream.isScreenShare}>
              Mute
            </button>
            <button
              className={BUTTON_STYLE_STREAM_PANEL_RIGHT}
              onClick={handleUnmuteVideo}
              hidden={stream.isScreenShare}
            >
              Unmute
            </button>
          </span>
        ) : (
          <button className={BUTTON_STYLE_STREAM_PANEL} onClick={handleAddVideo}>
            Add Video
          </button>
        )}
        {isExtractingFrames ? (
          <button className={BUTTON_STYLE_STREAM_PANEL} onClick={handleStopFrameExtraction}>
            No frames
          </button>
        ) : (
          <DropMenu label="Frames" items={FRAME_BUTTONS} style={BUTTON_STYLE_STREAM_PANEL} />
        )}
        <button className={BUTTON_STYLE_STREAM_PANEL} onClick={handleUpdateAdditionalTags}>
          Update Tags
        </button>
        <button className={BUTTON_STYLE_STREAM_PANEL} onClick={handleLogStats}>
          Log Stats
        </button>
        <button className={BUTTON_STYLE_STREAM_PANEL} onClick={handleStopPublish}>
          Stop
        </button>
      </div>
      <div className="w-full sm:w-48 h-12 sm:h-auto bg-white p-1 ">
        {isAudioMuted && <SpeakerXMarkIcon className="h-12 sm:h-24 text-red-500 p-1" />}
        <AudioMeter levelReader={audioLevelGetter} />
      </div>
      <div className="w-full flex flex-row sm:rounded-r-md items-center bg-gray-300">
        <MicrophoneIcon className="h-12 sm:h-24 text-gray-600 py-2" />
        {stream.isScreenShare && <ComputerDesktopIcon className="h-12 sm:h-24 text-gray-600 py-2" />}
        <p className="p-1 w-full text-gray-600 text-sm bold">
          userId: <b>{stream.userId}</b> <br />
          metadata: <b>{stream.metadata?.dataValue}</b>
        </p>
        <img id={frameElementId} src={undefined} />
      </div>
      {stream.videoCamera && <Video source={stream} isMuted={isVideoMuted} isMirrored />}
      {stream.screenShare && <Video source={stream} isMuted={isVideoMuted} />}
    </div>
  );
}
